import React, { FunctionComponent, useCallback } from 'react';
import { useLocation, useRouteMatch } from 'react-router';
import { Form } from 'react-final-form';
import { parse, originUrl } from '../../util/api.util';
import { MagicLinkContainer } from './MagicLinkContainer';
import {
  MagicLinkRequestPageFormValues,
  MagicLinkRequestPageFormErrors,
} from './MagicLinkRequestPage.types';
import { isValidEmail } from '../../util/validator.util';
import { coreapi } from '../../api';
import { history } from '../../history';
import { useToaster } from '../../hooks';
import { Input, Group, Field, FieldError, Button } from '../../components';
import styled from 'styled-components';

const StyledForm = styled.form`
  button {
    margin: 1rem 0;
  }

  > * {
    margin-bottom: 1rem;
  }

  @media (max-width: 1000px) {
    > * {
      margin-bottom: 0px;
    }
  }
`;

const handleValidate = (
  values: MagicLinkRequestPageFormValues,
): MagicLinkRequestPageFormErrors => {
  const errors = {} as MagicLinkRequestPageFormErrors;

  if (!values.email) {
    errors.email = 'Required';
  } else if (!isValidEmail(values.email)) {
    errors.email = 'Please use a valid email';
  }

  return errors;
};

export const MagicLinkRequestPage: FunctionComponent = () => {
  const { toast, toaster } = useToaster();
  const {
    params: { previousToken },
  } = useRouteMatch<Record<string, any>>();
  const { search } = useLocation();
  const { email, message } = parse(search);

  const handleSubmit = useCallback(
    async (values: MagicLinkRequestPageFormValues) => {
      const payload: { [key: string]: string } = {
        app_url: originUrl('sso/magiclink/'),
        email: values.email,
      };

      if (previousToken) {
        payload.previous_token = previousToken;
      }

      try {
        await coreapi.put(null!, `/magiclink`, payload);
        history.push(`/magiclink/sent?email=${values.email}`);
      } catch (error) {
        toaster(
          toast.error(error, 'There was a problem sending the magic link.'),
        );
      }
    },
    [previousToken, toast, toaster],
  );

  return (
    <MagicLinkContainer
      id="magic-link-request-page"
      data-testid="magic-link-request-page"
    >
      <Form
        initialValues={{ email }}
        onSubmit={handleSubmit}
        validate={handleValidate}
      >
        {formProps => {
          return (
            <StyledForm onSubmit={formProps.handleSubmit}>
              <strong>Request Magic Link</strong>
              <p>
                {message ||
                  'Please enter your email below to request a new magic link to automatically log in.'}
              </p>
              <Group direction="column">
                <Field name="email" label="Email">
                  {props => <Input {...props.input} className="-required" />}
                </Field>
                <FieldError name="email" />
              </Group>
              <Button
                type="submit"
                icon="fas fa-paper-plane"
                id="magic-link-request-page-submit-button"
                data-testid="magic-link-request-page-submit-button"
                disabled={formProps.submitting}
                loading={formProps.submitting}
              >
                <span>Send Magic Link</span>
              </Button>
            </StyledForm>
          );
        }}
      </Form>
    </MagicLinkContainer>
  );
};
